/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.14.0
 */

import { Fragment, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';

import { icons } from '../../../../../assets';
import { Button, EllipseText, Skeleton } from '../../../../../components/common';
import { ImgStyled } from '../../../../../components/common/Block/Img';
import SimpleTable, { ISimpleTableColumns, ISimpleTableData } from '../../../../../components/SimpleTable/SimpleTable';
import { makeSelectTranslationCommon } from '../../../../../modules/internationalization/intSelectors';
import { modalOpen } from '../../../../../modules/modal/modalActions';
import declination from '../../../../../utils/declination';
import { useSubscriberIdContext } from '../../../context';
import { ESubscriberIdActions } from '../../../types';
import SubscriberIdError from '../../SubscriberIdError';
import { manageNumberTranslationSelector } from '../selectors';
import { useGetManageNumberMetaQuery, useGetManageNumberRedirectionQuery } from '../store';
import { IManageNumberRedirection } from '../types';
import ManageNumberRedirectionModalAdd from './ManageNumberRedirectionModalAdd';
import ManageNumberRedirectionModalRemove from './ManageNumberRedirectionModalRemove';
import { ManageNumberRedirectionSkeleton } from './ManageNumberSkeleton';
import { ManageNumberRedirectionStyled } from './styled/ManageNumberStyled';


const tableColumns: ISimpleTableColumns[] = [
    { id: 'edit', title: '', style: { minWidth: 20 } },
    { id: 'msisdn', title: '', style: { width: 120 } },
    { id: 'status', title: '', style: { width: 150 } },
    { id: 'description', title: '', style: { width: 400 } },
    { id: 'remove', title: '', style: { minWidth: 20 } },
];

const ManageNumberRedirection = () => {

    const { subscriberId } = useSubscriberIdContext();

    const dispatch = useDispatch();

    /* Select State */

    const translationCommon = useSelector(makeSelectTranslationCommon());
    const translationManageNumber = useSelector(manageNumberTranslationSelector());

    /* Local State */

    const [force, setForce] = useState<boolean>(false);

    /* Api */

    const { isChangeAvailable } = useGetManageNumberMetaQuery(subscriberId, {
        selectFromResult: ({ data }) => ({
            isChangeAvailable: data?.actions?.includes(ESubscriberIdActions.forwardingChange),
        }),
    });

    const { data, error, isFetching, refetch } = useGetManageNumberRedirectionQuery({ subscriberId, force });

    /* Handlers */

    const onRemoveRedirection = (item: IManageNumberRedirection) => {
        dispatch(modalOpen({
            render: props => <ManageNumberRedirectionModalRemove
                item={item}
                subscriberId={subscriberId}
                onCloseModal={props.onCloseModal}
            />,
        }));
    };

    const onAddRedirection = () => {
        dispatch(logBusinessEvent({
            eventName: ELogBusinessEventName.USER_SHOW_REDIRECT_POPUP,
            type: ELogBusinessEventType.common,
        }))
        dispatch(modalOpen({
            render: props => <ManageNumberRedirectionModalAdd
                subscriberId={subscriberId}
                onCloseModal={props.onCloseModal}
            />,
        }));
    };

    const updateRedirection = () => {
        force ? refetch() : setForce(true);
    };

    if (error) return <SubscriberIdError refetch={refetch}/>;
    if (isFetching) return <Skeleton isLoading render={ManageNumberRedirectionSkeleton}/>;

    const tableData: ISimpleTableData[] = data?.map((row: IManageNumberRedirection) => ({
        id: row.type,
        edit: <span style={{ display: 'flex' }}>
                <ImgStyled className="medium pointer" src={icons.arrowRightIcon}/>
            </span>,
        msisdn: row.msisdn,
        status: <div>
            {translationManageNumber.redirection.table.status[row.type]}
            {!!row.sec && <div>({row.sec} {declination(row.sec, translationCommon.seconds)})</div>}
        </div>,
        description: <EllipseText
            lines={5}
            title={translationManageNumber.redirection.table.description[row.type]}
        >
            {translationManageNumber.redirection.table.description[row.type]}
        </EllipseText>,
        remove: isChangeAvailable ? <span
            style={{ display: 'flex' }}
            onClick={() => onRemoveRedirection(row)}
        >
                <ImgStyled className="small pointer" src={icons.removeSmallIcon}/>
            </span> : <Fragment/>,
    })) ?? [];

    return (
        <ManageNumberRedirectionStyled>
            <div className="redirection__header">
                <div className="subscriber-id__title subscriber-id__title_bold">
                    {translationManageNumber.redirection.table.title}
                </div>
                <div className="redirection__control">
                    <ImgStyled
                        className="medium pointer"
                        src={icons.syncBtnIcon}
                        width="32px"
                        onClick={updateRedirection}
                    />
                    {
                        isChangeAvailable && (
                            <Button onClick={onAddRedirection}>
                                {translationManageNumber.redirection.table.btnAdd}
                            </Button>
                        )
                    }
                </div>
            </div>
            {
                !!tableData.length
                    ? <SimpleTable columns={tableColumns} data={tableData}/>
                    : <div className="subscriber-id__title subscriber-id__title_bold">
                        {translationManageNumber.redirection.empty}
                    </div>
            }
        </ManageNumberRedirectionStyled>
    );
};

export default ManageNumberRedirection;
